import { apiClient, unwrap } from './client';
import { getAccessToken } from '@/auth/tokenStore';
import type {
  ApiEnvelope,
  EduEvent,
  EduFieldHelp,
  EduFieldHelpResult,
  EduFirstRunCompleteResult,
  EduFirstRunState,
  EduTour,
  EduToursResult,
} from './types';

/**
 * Education & Enablement (PS-5 Track C) — the `/v1/edu/*` surface. FROZEN CONTRACT with Track A.
 * Track A builds these in parallel; every call degrades to a no-op / hidden UI when the endpoint is
 * absent (errors.ts `isNotAvailable`) — never a crash, never a blocking modal on error (F-ED-01).
 *
 * Reads (tours / field-help / first-run) go through the shared `apiClient` (bearer + envelope). The
 * event SINK deliberately does NOT: see `sendEduEvents`.
 */

const EDU_EVENTS_URL = '/api/v1/edu/events';

/**
 * POST /v1/edu/events — the fire-and-forget instrumentation sink. Uses `fetch` (NOT the shared axios
 * client) on purpose:
 *   - `keepalive: true` lets an unload-time flush survive page teardown (the last-events problem);
 *   - it MUST NOT trip the axios 401 interceptor's reject-closed re-auth — telemetry can never log a
 *     user out or surface an error to the analyst (F-ED-01: instrumentation never blocks/breaks a
 *     user action). Every failure is swallowed; the batching client (eduClient) owns retry policy
 *     (there is none — dropped on failure, by design).
 * Returns true on a 2xx, false otherwise; never throws.
 */
export async function sendEduEvents(events: EduEvent[]): Promise<boolean> {
  if (events.length === 0) return true;
  try {
    const token = getAccessToken();
    const res = await fetch(EDU_EVENTS_URL, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ events }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** GET /v1/edu/tours?surface= — the tenancy-scoped tour definitions for a surface (tours are DATA). */
export async function getEduTours(surface: string): Promise<EduTour[]> {
  const { data } = await apiClient.get<ApiEnvelope<EduToursResult>>('/v1/edu/tours', {
    params: { surface },
  });
  const body = unwrap(data);
  return Array.isArray(body?.tours) ? body.tours : [];
}

/** GET /v1/edu/field-help?family= — what/why/example for a capability family's config fields. */
export async function getEduFieldHelp(family: string): Promise<EduFieldHelp[]> {
  const { data } = await apiClient.get<ApiEnvelope<EduFieldHelpResult>>('/v1/edu/field-help', {
    params: { family },
  });
  const body = unwrap(data);
  return Array.isArray(body?.fields) ? body.fields : [];
}

/** GET /v1/edu/first-run — whether to offer the flagship first-run, plus already-completed tour ids. */
export async function getEduFirstRun(): Promise<EduFirstRunState> {
  const { data } = await apiClient.get<ApiEnvelope<EduFirstRunState>>('/v1/edu/first-run');
  return unwrap(data);
}

/** POST /v1/edu/first-run/complete — mark a tour completed; returns the updated completed-id set. */
export async function completeEduFirstRun(tourId: string): Promise<string[]> {
  const { data } = await apiClient.post<ApiEnvelope<EduFirstRunCompleteResult>>(
    '/v1/edu/first-run/complete',
    { tourId },
  );
  const body = unwrap(data);
  return Array.isArray(body?.completedTourIds) ? body.completedTourIds : [];
}
